/**
 * Injected wallet provider discovery.
 *
 * Wallets are discovered through EIP-6963 (multi injected provider discovery),
 * falling back to the legacy `window.ethereum` injection. MetaMask Mobile's
 * in-app browser injects `window.ethereum` asynchronously and dispatches
 * `ethereum#initialized` once it is available, so discovery keeps polling
 * until the timeout elapses.
 */

export interface EIP6963ProviderInfo {
	uuid: string
	name: string
	icon: string
	rdns: string
}

export interface EIP6963ProviderDetail {
	info: EIP6963ProviderInfo
	provider: any
}

export const METAMASK_RDNS = 'io.metamask'

const PREFERRED_RDNS = [METAMASK_RDNS, 'io.metamask.flask', 'com.coinbase.wallet']
const POLL_INTERVAL = 100

export function discoverProvider(timeoutMs: number = 3000): Promise<any | null> {
	return new Promise((resolve) => {
		const announced: EIP6963ProviderDetail[] = []
		let settled = false
		let interval: ReturnType<typeof setInterval> | undefined
		let timeout: ReturnType<typeof setTimeout> | undefined

		const onAnnounce = (event: Event) => {
			const detail = (event as CustomEvent<EIP6963ProviderDetail>).detail
			if (detail?.provider && !announced.some((d) => d.info?.uuid === detail.info?.uuid)) {
				announced.push(detail)
			}
		}

		const finish = (provider: any | null) => {
			if (settled) return
			settled = true

			clearInterval(interval)
			clearTimeout(timeout)
			window.removeEventListener('eip6963:announceProvider', onAnnounce)
			window.removeEventListener('ethereum#initialized', check)

			resolve(provider)
		}

		const pick = (): any | null => {
			for (const rdns of PREFERRED_RDNS) {
				const match = announced.find((d) => d.info?.rdns === rdns)
				if (match) return match.provider
			}

			if (announced.length > 0) {
				return announced[0].provider
			}

			return window.ethereum ?? null
		}

		function check() {
			const provider = pick()
			if (provider) {
				finish(provider)
			}
		}

		window.addEventListener('eip6963:announceProvider', onAnnounce)
		window.addEventListener('ethereum#initialized', check)
		window.dispatchEvent(new Event('eip6963:requestProvider'))

		interval = setInterval(check, POLL_INTERVAL)
		timeout = setTimeout(() => finish(null), timeoutMs)
	})
}
